/**
 * Site Visit Counter Widget for CEO Dashboard
 * Shows total visits, today's visits, and weekly/monthly stats
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Eye, 
  TrendingUp, 
  Calendar, 
  Clock,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VisitCounterWidget = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    // Refresh every 5 minutes
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/metrics/visits/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch visit stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-24 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white border-0">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <Eye className="h-5 w-5" />
          Site Visits
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Main stat - Total visits */}
        <div className="mb-4">
          <p className="text-4xl font-bold">{formatNumber(stats?.total_visits)}</p>
          <p className="text-violet-200 text-sm">Total visits</p>
        </div>
        
        {/* Secondary stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center gap-1 text-violet-200 text-xs mb-1">
              <Clock className="h-3 w-3" />
              Today
            </div>
            <p className="text-xl font-semibold">{formatNumber(stats?.today_visits)}</p>
          </div>
          
          <div className="bg-white/10 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center gap-1 text-violet-200 text-xs mb-1">
              <Calendar className="h-3 w-3" />
              7 Days
            </div>
            <p className="text-xl font-semibold">{formatNumber(stats?.week_visits)}</p>
          </div>
          
          <div className="bg-white/10 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center gap-1 text-violet-200 text-xs mb-1">
              <TrendingUp className="h-3 w-3" />
              30 Days
            </div>
            <p className="text-xl font-semibold">{formatNumber(stats?.month_visits)}</p>
          </div>
        </div>
        
        <button 
          onClick={fetchStats}
          className="mt-3 text-xs text-violet-200 hover:text-white flex items-center gap-1 mx-auto"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </CardContent>
    </Card>
  );
};

export default VisitCounterWidget;
